import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { services } from "@/data/homeData";

export function BentoServicesGrid() {
  return (
    <section className="py-20 md:py-28 bg-[#F5F0EB] overflow-hidden" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-14">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <motion.p initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-[#C9922A] uppercase tracking-[0.2em] text-sm font-semibold mb-3">
              Nos services
            </motion.p>
            <motion.h2 initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="text-3xl md:text-4xl text-[#004333] font-display">
              Une gestion complète de A à Z
            </motion.h2>
          </div>
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="text-[#2D2418]/50 max-w-md text-sm">
            De l'accueil des voyageurs à l'entretien de votre bien, nous prenons en charge toutes les prestations.
          </motion.p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 auto-rows-[180px] gap-4">
          {services.map((service, index) => {
            const Icon = service.icon;
            const sizeClass = index === 0 ? "sm:col-span-2 sm:row-span-2" : index === 5 ? "sm:col-span-2" : "";
            const isLarge = index === 0;
            const isWide = index === 5;

            return (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ delay: index * 0.05, duration: 0.4 }}
                className={`group relative rounded-2xl overflow-hidden cursor-pointer ${sizeClass}`}
                data-testid={`service-item-${index}`}
              >
                <img src={service.image} alt={service.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#004333]/80 via-[#004333]/30 to-[#004333]/10 transition-opacity duration-500 group-hover:opacity-0" />
                <div className="absolute inset-0 bg-[#004333]/90 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                {/* Default state */}
                <div className="absolute inset-0 p-5 flex flex-col justify-between transition-opacity duration-400 group-hover:opacity-0">
                  <div className="flex items-start justify-between">
                    <div className="w-10 h-10 rounded-xl bg-[#C9922A] flex items-center justify-center shadow-[0_4px_16px_rgba(201,146,42,0.3)]">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="font-mono text-xs text-white/30 font-bold">{String(index + 1).padStart(2, "0")}</span>
                  </div>
                  <div>
                    <h3 className={`font-display text-white leading-snug ${isLarge ? "text-2xl mb-2" : "text-base"}`}>{service.title}</h3>
                    {(isLarge || isWide) && service.stat && (
                      <div className="flex items-center gap-2 mt-2">
                        <span className="font-mono text-[#C9922A] font-black text-lg">{service.stat.value}</span>
                        <span className="text-white/40 text-xs uppercase tracking-wider">{service.stat.label}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Hover state */}
                <div className="absolute inset-0 p-5 flex flex-col justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-[#C9922A] flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="font-display text-white text-base leading-tight">{service.title}</h3>
                  </div>
                  <p className={`text-white/70 leading-relaxed mb-3 ${isLarge || isWide ? "text-sm" : "text-xs line-clamp-3"}`}>{service.description}</p>
                  {(isLarge || isWide) && service.highlights && (
                    <ul className="space-y-1.5">
                      {service.highlights.map((h, i) => (
                        <li key={i} className="flex items-center gap-2 text-white/60 text-xs">
                          <span className="w-1 h-1 rounded-full bg-[#C9922A] flex-shrink-0" />{h}
                        </li>
                      ))}
                    </ul>
                  )}
                  {service.stat && (
                    <div className="flex items-center gap-2 mt-auto pt-3 border-t border-white/10">
                      <span className="font-mono text-[#C9922A] font-black text-sm">{service.stat.value}</span>
                      <span className="text-white/40 text-[10px] uppercase tracking-wider">{service.stat.label}</span>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-[#2D2418]/30 text-sm hidden md:block">8 prestations incluses dans notre offre de gestion</p>
        <Link to="/expertise" className="inline-flex items-center gap-2 px-6 py-3 border-2 border-[#004333] text-[#004333] rounded-full text-sm font-semibold uppercase tracking-wider hover:bg-[#004333] hover:text-white transition-all duration-300">
          Découvrir notre méthode <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}
